import { AddIcon, CloseIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Text,
  HStack,
  Th,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  List,
  ListItem,
  Spinner,
  Portal,
  Switch,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { appRequest } from "routes";
import CustomInput from "components/common/customInput/CustomInput";
import { addNA, addNAObject, addWithoutNA } from "../../common/function";
import {
  invoiceTypeOptions,
  currencyOptions
} from "../../common/constant";
import { foreignRegions } from "./constant";
import { AppContext } from "AppProvider/AppContext";
import { customDecimalRoundSafeFunction } from "../../common/ViewerModal/utils/function";
import { extractPercentageFromLabel } from "utils/functions/function";

const COMPANY_CODES = [
  "EBL",
  "EMX",
  "ENL",
  "ELPL",
  "ENZ",
  "EPC",
  "ESA",
  "EPE",
  "EPP",
  "EWPL",
  "EAU",
  "EPL",
  "EMN",
  "EPDR",
  "EUK",
  "EBT",
  "EMCT",
  "EUS",
  "AVL",
  "LPK",
  "MNPI",
  "MPI",
  "TII",
  "TIL",
  "TIM",
  "ZHL"
];

const companyOptions = [
  { label: "Avet Lifesciences Pvt Ltd", value: "AVL" },
  { label: "Emcure Australia", value: "EAU" },
  { label: "Emcure Brasil", value: "EBL" },
  { label: "Gennova Biopharmaceutical", value: "EBT" },
  { label: "Emcure Life Science Pvt L", value: "ELPL" },
  { label: "Emcutix Biopharmaceutical", value: "EMCT" },
  { label: "Emcure Pharmaceutical Men", value: "EMN" },
  { label: "Emcure Mexico", value: "EMX" },
  { label: "Emcure Nigeria Limited", value: "ENL" },
  { label: "Emcure NZ Ltd", value: "ENZ" },
  { label: "EMCURE PHARMA CHILE S.P.A", value: "EPC" },
  { label: "EMCURE PHARMACEUTICALS", value: "EPDR" },
  { label: "Emcure Pharma Peru S.A.C.", value: "EPE" },
  { label: "Emcure Pharmaceuticals", value: "EPL" },
  { label: "Emcure Pharma Philippines", value: "EPP" },
  { label: "Emcure South Africa", value: "ESA" },
  { label: "Emcure Pharma UK Ltd.", value: "EUK" },
  { label: "Avet Pharma Labs Inc.", value: "EUS" },
  { label: "Emcure Wellness Private", value: "EWPL" },
  { label: "Lazor Pharmaceuticals Ltd", value: "LPK" },
  { label: "Mantra Pharma Inc", value: "MNPI" },
  { label: "Marcan Pharmaceuticals In", value: "MPI" },
  { label: "Tillomed Italia S.R.L.", value: "TII" },
  { label: "Tillomed Malta Ltd", value: "TIM" }
];

const editableInputProps = {
  size: "sm",
  h: "36px",
  bg: "gray.50",
  borderRadius: "md",
  borderColor: "gray.300",
  _hover: { bg: "white", borderColor: "gray.400" },
  _focus: {
    borderColor: "blue.500",
    boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.3)",
  },
};

interface LineItem {
  glCode: string;
  glDescription: string;
  glAmount: string | number;
  taxCode: string;
  costCenter: string;
  lineNarration: string;
  internalOrder: string;
  businessArea: string;
  drCrIndicator: "D" | "C" | "";
  plantCode: string;
  profitCenter: string;
  hsnCode: string;
  rowId: string;
  costCenterOptions?: any[];
  internalOrderOptions?: any[];
  businessAreaOptions?: any[];
  gstPercentage?: any;
  tdsType?: any;
  tdsPercentage?: number | string;
  tdsAmount?: number | string;
  tdsRemarks?: string;
  grossingUp?: string;
  businessPlaceOptions?: any[];
  tdsSectionType?: any;
  businessPlace?: string;
}

interface ForeignFormProps {
  value: any;
  onChange: (updated: any) => void;
  workflowProcess?: any;
  showTds?: any;
  tdsFifthApproval?: any;
  disabledOtherUserField?: any;
  gstUser?: any;
}

export default function ForeignForm({
  value = {},
  onChange,
  workflowProcess,
  showTds,
  tdsFifthApproval,
  disabledOtherUserField,
  gstUser,
}: ForeignFormProps) {
  const { accountDetails } = useContext(AppContext);
  const [form, setForm] = useState<any>({
    gstPartner: "",
    region: undefined,
    postingDate: new Date(),
    exchangeRate: "",
    invoiceType: null,
    currency: "",
    headerText: "",
    referenceKey1: "",
    referenceKey2: "",
    referenceKey3: "",
    tdsTaxCode: null,
    basicAmountForTDS: "",
    companyCode: null,
    vendorCode: "",
    vendorName: "",
    vendorAddress: "",
    invoiceNo: "",
    invoiceDate: "",
    grandTotal: "",
    taxIdenficationNumber: "",
    paymentDueDate: "",
    lineItems: [
      {
        glCode: null,
        glDescription: "",
        glAmount: "",
        taxCode: null,
        costCenter: null,
        lineNarration: "",
        internalOrder: null,
        businessArea: null,
        drCrIndicator: null,
        plantCode: null,
        profitCenter: null,
        hsnCode: "",
        rowId: Date.now().toString(),
        gstPercentage: 0,
        tdsType: "",
        tdsPercentage: 0,
        tdsAmount: 0,
        tdsRemarks: "",
        grossingUp: "",
        tdsSectionType: { label: "NA", value: "NA" },
        businessPlace: "",
        businessPlaceOptions: [],
      },
    ],
    ...value,
  });

  const [vendorSearch, setVendorSearch] = useState(value.vendorCode || "");
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [showVendorList, setShowVendorList] = useState(false);
  const [isVendorLoading, setIsVendorLoading] = useState(false);

  // Shared Options State
  const [pcOptions, setPcOptions] = useState<any[]>([]);
  const [businessPlaceOptions, setBusinessPlaceOptions] = useState<any[]>([]);

  const [glOptions, setGlOptions] = useState<any[]>([]);
  const [taxOptions, setTaxOptions] = useState<any[]>([]);
  const [plantOptions, setPlantOptions] = useState<any[]>([]);
  const [tdsTaxCodeOptions, setTdsTaxCodeOptions] = useState<any[]>([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [dynamicRegions, setDynamicRegions] = useState<any[]>([]);
  const toast = useToast();

  // Container refs for Portal positioning and click-outside
  const vendorContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const vendorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const masterDataTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Stability refs
  const onChangeRef = useRef(onChange);
  const workflowProcessRef = useRef(workflowProcess);
  const lastFetchedCode = useRef("");
  const isInternalUpdating = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    workflowProcessRef.current = workflowProcess;
  }, [workflowProcess]);

  // Click outside to close vendor list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isInsideInput = vendorContainerRef.current?.contains(
        event.target as Node,
      );
      const isInsideDropdown = dropdownRef.current?.contains(
        event.target as Node,
      );

      if (!isInsideInput && !isInsideDropdown) {
        setShowVendorList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateForm = useCallback((updates: any | ((prev: any) => any)) => {
    setForm((prev: any) => {
      const next =
        typeof updates === "function" ? updates(prev) : { ...prev, ...updates };

      const cleanData = (obj: any) => {
        const { lineItems, ...rest } = obj;

        // Clean header fields (keep objects if present)
        const cleanedHeader: any = { ...rest };
        // No more flattening to .value here. The parent BSES POC expects objects.

        // Clean line items
        const cleanedItems = (lineItems || []).map((li: any) => {
          const {
            profitCenterOptions,
            costCenterOptions,
            glCodeOptions,
            internalOrderOptions,
            plantCodeOptions,
            businessAreaOptions,
            taxCodeOptions,
            businessPlaceOptions, // Added
            ...data
          } = li;

          // Keep objects for parent state
          return data;
        });
        return { ...cleanedHeader, lineItems: cleanedItems };
      };

      const cleanedNext = cleanData(next);
      if (JSON.stringify(cleanData(prev)) !== JSON.stringify(cleanedNext)) {
        // Use a ref to prevent sync useEffect from running immediately
        isInternalUpdating.current = true;
        onChangeRef.current(cleanedNext);
        // Clear flag after a short delay
        if ((window as any)._formSyncTimer)
          clearTimeout((window as any)._formSyncTimer);
        (window as any)._formSyncTimer = setTimeout(() => {
          isInternalUpdating.current = false;
        }, 300);
      }
      return next;
    });
  }, []);

  const updateLineItem = useCallback(
    (index: number, updates: Partial<LineItem>) => {
      updateForm((prev: any) => {
        const newLineItems = [...prev.lineItems];
        if (newLineItems[index]) {
          newLineItems[index] = { ...newLineItems[index], ...updates };
        }
        return { ...prev, lineItems: newLineItems };
      });
    },
    [updateForm],
  );

  // Sync from Parent
  useEffect(() => {
    if (isInternalUpdating.current) return;
    setForm((prev: any) => {
      const { lineItems: prevItems, ...prevData } = prev;
      const { lineItems: nextItems, ...nextData } = value;

      const normalize = (items: any[] = []) =>
        items.map((i) => {
          const {
            profitCenterOptions,
            costCenterOptions,
            glCodeOptions,
            internalOrderOptions,
            plantCodeOptions,
            businessAreaOptions,
            taxCodeOptions,
            businessPlaceOptions, // Added
            ...rest
          } = i;
          return rest;
        });

      const cleanedNext = normalize(nextItems);
      const cleanedPrev = normalize(prevItems);

      if (
        JSON.stringify(prevData) === JSON.stringify(nextData) &&
        JSON.stringify(cleanedPrev) === JSON.stringify(cleanedNext)
      )
        return prev;

      return {
        ...prev,
        ...nextData,
        lineItems: (nextItems || prevItems || []).map(
          (item: any, idx: number) => {
            const prevItem = prevItems[idx] || {};
            // Merge incoming data with existing options if present
            return {
              ...prevItem,
              ...item,
            };
          },
        ),
      };
    });
    if (value.vendorCode !== undefined && value.vendorCode !== vendorSearch)
      setVendorSearch(value.vendorCode || "");
  }, [value]);

  // Fetch Foreign Regions dynamically based on profileId
  useEffect(() => {
    const fetchRegions = async () => {
      if (!accountDetails?.profileId) return;
      try {
        const res: any = await appRequest("user", "getForeignRegionsByUser", {
          profileId: accountDetails.profileId,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          const formattedRegions = Array.isArray(res?.data)
            ? res.data.map((item: any) => {
              const parts = typeof item?.label === "string" ? item.label.split("-") : [];
              return {
                ...item,
                label: parts.length > 2 ? parts.slice(2).join("-") : item?.label || "",
              };
            })
            : [];
          setDynamicRegions(formattedRegions);
        }
      } catch (err) {
        console.error("Failed to fetch foreign regions:", err);
      }
    };

    fetchRegions();
  }, [accountDetails?.profileId]);

  // ───────────────────────────────────────────────
  // Master Data Fetch (Lazy & Reactive)
  // ───────────────────────────────────────────────

  const fetchProfitCenterOptions = useCallback(
    async (isManual = false) => {
      const code =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : (form.companyCode || "").trim();
      if (!code) return;
      setIsOptionsLoading(true);
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getProfitCenter",
          companyCode: code,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          setPcOptions(
            res.data.map((pc: any) => ({
              label: pc.label || pc.profitCenter,
              value: pc.profitCenter,
            })),
          );
          if (isManual) {
            toast({
              title: "Profit Centers Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("PC fetch fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, toast],
  );

  const fetchGlOptions = useCallback(
    async (isManual = false) => {
      const code =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : (form.companyCode || "").trim();
      if (!code) return;
      setIsOptionsLoading(true);
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getGlAccount",
          companyCode: code,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          const options = res.data.map((gl: any) => ({
            label: gl.glCode || gl.glAccount || gl.label || "",
            value: gl.glCode || gl.glAccount || gl.value || "",
            description:
              gl.description ||
              gl.glDescription ||
              gl.label ||
              gl.profitCenterDescription ||
              "",
          }));
          setGlOptions(options);
          if (isManual) {
            toast({
              title: "GL Accounts Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("GL fetch fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, toast],
  );

  const fetchPlantOptions = useCallback(
    async (isManual = false) => {
      const code =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : (form.companyCode || "").trim();
      if (!code) return;
      setIsOptionsLoading(true);
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getPlantCode",
          companyCode: code,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          setPlantOptions(
            res.data.map((p: any) => ({
              label:
                typeof p === "string" ? p : p.label || p.plantCode || p.value,
              value: typeof p === "string" ? p : p.plantCode || p.value,
            })),
          );
          if (isManual) {
            toast({
              title: "Plant Codes Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("Plant fetch fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, toast],
  );

  const fetchTaxOptions = useCallback(
    async (isManual = false) => {
      const code =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : (form.companyCode || "").trim();
      if (!code) return;
      setIsOptionsLoading(true);
      try {
        const res: any = await appRequest("taxcode", "getAll", {
          page: 1,
          limit: 250,
          process: workflowProcessRef.current?.processName,
          companyCode: code,
        });
        if (res?.status === "success") {
          setTaxOptions(
            (res?.data?.data || []).map((t: any) => ({
              label: t.label || t.taxCode || t.value,
              value: t.value,
              taxCodes: t.taxCodes || [],
            })),
          );
          if (isManual) {
            toast({
              title: "Tax Codes Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("Tax fetch fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, toast],
  );

  // Handle Company Code Change -> Reset Options and Fetch New
  useEffect(() => {
    const code =
      typeof form.companyCode === "object"
        ? form.companyCode?.value
        : (form.companyCode || "").trim();
    if (code && code !== lastFetchedCode.current) {
      lastFetchedCode.current = code;
      setPcOptions([]);
      setGlOptions([]);
      setPlantOptions([]);
      setTaxOptions([]);
      setBusinessPlaceOptions([])
      fetchProfitCenterOptions();
      fetchBusinessPlaceOptions()
      fetchGlOptions();
      fetchPlantOptions();
      fetchTaxOptions();
    }
  }, [
    form.companyCode,
    fetchProfitCenterOptions,
    fetchGlOptions,
    fetchPlantOptions,
    fetchTaxOptions,
  ]);

  // ───────────────────────────────────────────────
  // Vendor Search logic (Filtered by Company Code)
  // ───────────────────────────────────────────────
  useEffect(() => {
    if (vendorTimerRef.current) clearTimeout(vendorTimerRef.current);
    const trimmed = vendorSearch.trim();

    // Don't search if it looks like a code we just selected or is too short
    if (!trimmed || trimmed.length < 2 || trimmed === form.vendorCode) {
      setShowVendorList(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsVendorLoading(true);
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getVendorByName",
          vendorName: trimmed,
          companyCode: form.companyCode?.value,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          setVendorList(res.data);
          setShowVendorList(res.data.length > 0);
        } else {
          setVendorList([]);
          setShowVendorList(false);
        }
      } catch (err) {
        console.error("Vendor fetch fail:", err);
      } finally {
        setIsVendorLoading(false);
      }
    }, 600);

    vendorTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [vendorSearch, form.vendorCode, form.companyCode]);

  // ───────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────

  const handleSelectVendor = (vendor: any) => {
    const vCode = vendor.vendorCode || "";
    const vName = vendor.supplierName || vendor.label || "";
    const vAddr = vendor.vendorAddress || "";

    updateForm({
      vendorCode: vCode,
      vendorName: vName,
      vendorAddress: vAddr,
      companyCode: vendor.companyCode || form.companyCode,
    });
    setVendorSearch(vCode);
    setShowVendorList(false);
  };

  const fetchInternalOrderOptions = useCallback(
    async (index: number, comp: any, pc: any, gl: any, isManual = false) => {
      const compVal = typeof comp === "object" ? comp?.value : comp;
      const pcVal = typeof pc === "object" ? pc?.value : pc;
      const glVal = typeof gl === "object" ? gl?.value : gl;
      if (!compVal || !pcVal || !glVal) return;
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getInternalOrder",
          companyCode: compVal,
          glAccount: glVal,
          profitCenter: pcVal,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          updateLineItem(index, {
            internalOrderOptions: res.data.map((io: string) => ({
              label: io,
              value: io,
            })),
          });
          if (isManual) {
            toast({
              title: "Internal Orders Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        /* console.error("IO fail:", err); */
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [updateLineItem, toast],
  );

  const fetchCostCenterOptions = useCallback(
    async (index: number, prof: any, isManual = false) => {
      const profVal = typeof prof === "object" ? prof?.value : prof;
      const compVal =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : form.companyCode;
      if (!profVal || !compVal) return;
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getCostCenter",
          companyCode: compVal,
          profitCenter: profVal,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          updateLineItem(index, {
            costCenterOptions: res.data.map((cc: any) => ({
              label: cc.label || cc.costCenter,
              value: cc.costCenter,
            })),
          });
          if (isManual) {
            toast({
              title: "Cost Centers Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("CC fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, updateLineItem, toast],
  );

  const fetchBusinessAreaOptions = useCallback(
    async (index: number, prof: any, cost: any, isManual = false) => {
      const profVal = typeof prof === "object" ? prof?.value : prof;
      const costVal = typeof cost === "object" ? cost?.value : cost;
      const compVal =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : form.companyCode;
      if (!profVal || !costVal || !compVal) return;
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getBusinessArea",
          companyCode: compVal,
          profitCenter: profVal,
          costCenter: costVal,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          updateLineItem(index, {
            businessAreaOptions: res.data.map((ba: string) => ({
              label: ba,
              value: ba,
            })),
          });
          if (isManual) {
            toast({
              title: "Business Areas Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("BA fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, updateLineItem, toast],
  );

  const fetchBusinessPlaceOptions = useCallback(
    async (isManual = false) => {
      const compVal =
        typeof form.companyCode === "object"
          ? form.companyCode?.value
          : form.companyCode;

      console.log("Fetching BP Options:", {
        compVal,
        vendorCode: form.vendorCode,
      });

      if (!compVal) {
        console.warn("BP Fetch Aborted: Missing plant or company code");
        return;
      }
      try {
        const res: any = await appRequest("user", "masterFinancialApi", {
          type: "getBusinessPlace",
          companyCode: compVal,
          vendorCode: form.vendorCode,
          ...workflowProcessRef.current,
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          setBusinessPlaceOptions(res.data.map((bp: string) => ({
            label: bp,
            value: bp,
          })));
          if (isManual) {
            toast({
              title: "Business Places Refreshed",
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }
      } catch (err) {
        console.error("Business Place fetch fail:", err);
      } finally {
        setIsOptionsLoading(false);
      }
    },
    [form.companyCode, form.vendorCode, updateLineItem, toast],
  );

  const handleProfitCenterChange = async (index: number, val: any) => {
    const item = form.lineItems[index];
    const prevVal =
      typeof item?.profitCenter === "object"
        ? item.profitCenter?.value
        : item?.profitCenter;
    const nextVal = typeof val === "object" ? val?.value : val;

    if (nextVal === prevVal) return;

    updateLineItem(index, {
      profitCenter: val,
      costCenter: "",
      costCenterOptions: [],
      internalOrder: "",
      internalOrderOptions: [],
      businessArea: "",
      businessAreaOptions: [],
    });
    if (nextVal) {
      fetchCostCenterOptions(index, nextVal);
      const glVal =
        typeof item?.glCode === "object" ? item.glCode?.value : item?.glCode;
      if (glVal)
        fetchInternalOrderOptions(index, form.companyCode, nextVal, glVal);
    }
  };

  const handleCostCenterChange = async (index: number, val: any) => {
    const item = form.lineItems[index];
    const prevVal =
      typeof item?.costCenter === "object"
        ? item.costCenter?.value
        : item?.costCenter;
    const nextVal = typeof val === "object" ? val?.value : val;

    if (nextVal === prevVal) return;

    updateLineItem(index, {
      costCenter: val,
      businessArea: "",
      businessAreaOptions: [],
    });
    const pcVal =
      typeof item?.profitCenter === "object"
        ? item.profitCenter?.value
        : item?.profitCenter;
    if (nextVal && pcVal) {
      fetchBusinessAreaOptions(index, pcVal, nextVal);
    }
  };

  const handleGlCodeChange = async (index: number, val: any) => {
    const item = form.lineItems[index];
    const prevVal =
      typeof item?.glCode === "object" ? item.glCode?.value : item?.glCode;
    const nextVal = typeof val === "object" ? val?.value : val;

    if (nextVal === prevVal) return;

    const desc = glOptions.find((o) => o.value === nextVal)?.description || "";
    updateLineItem(index, {
      glCode: val,
      glDescription: desc,
      internalOrder: "",
      internalOrderOptions: [],
    });
    const pcVal =
      typeof item?.profitCenter === "object"
        ? item.profitCenter?.value
        : item?.profitCenter;
    const compVal =
      typeof form.companyCode === "object"
        ? form.companyCode?.value
        : form.companyCode;
    if (nextVal && pcVal && compVal) {
      fetchInternalOrderOptions(index, compVal, pcVal, nextVal);
    }
  };

  const handleInvoiceTypeChange = (val: any) => {
    const typeValue = val?.value;
    updateForm((prev: any) => {
      const newLineItems = prev.lineItems.map((item: any) => {
        let updatedDrCr = item.drCrIndicator;
        if (typeValue === "KG") {
          updatedDrCr = { label: "Credit", value: "H" };
        } else if (typeValue === "KR") {
          updatedDrCr = { label: "Debit", value: "S" };
        }
        return { ...item, drCrIndicator: updatedDrCr };
      });
      return { ...prev, invoiceType: val, lineItems: newLineItems };
    });
  };

  const handlePlantCodeChange = async (index: number, val: any) => {
    const item = form.lineItems[index];
    const prevVal =
      typeof item?.plantCode === "object"
        ? item.plantCode?.value
        : item?.plantCode;
    const nextVal = typeof val === "object" ? val?.value : val;

    if (nextVal === prevVal) return;

    updateLineItem(index, {
      plantCode: val
    });
    if (nextVal) {
      // fetchBusinessPlaceOptions(index, nextVal);
    }
  };

  const addLineItem = () => {
    const typeValue = form.invoiceType?.value;
    let defaultDrCr: any = null;
    if (typeValue === "KG") {
      defaultDrCr = { label: "Credit", value: "H" };
    } else if (typeValue === "KR") {
      defaultDrCr = { label: "Debit", value: "S" };
    }

    updateForm((prev: any) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          glCode: null,
          glDescription: "",
          glAmount: "",
          taxCode: null,
          costCenter: null,
          lineNarration: "",
          internalOrder: null,
          businessArea: null,
          drCrIndicator: defaultDrCr,
          plantCode: null,
          profitCenter: null,
          hsnCode: "",
          rowId:
            Date.now().toString() + Math.random().toString(36).substr(2, 9),
          reject: false,
          costCenterOptions: [],
          internalOrderOptions: [],
          businessAreaOptions: [],
          gstPercentage: 0,
          tdsType: "",
          tdsPercentage: 0,
          tdsAmount: 0,
          tdsRemarks: "",
          grossingUp: "",
          tdsSectionType: { label: "NA", value: "NA" },
          businessPlace: "",
          businessPlaceOptions: [],
        },
      ],
    }));
  };

  const removeLineItem = (index: number) => {
    updateForm((prev: any) =>
      prev.lineItems.length > 1
        ? {
          ...prev,
          lineItems: prev.lineItems.filter(
            (_: any, i: number) => i !== index,
          ),
        }
        : prev,
    );
  };

  // ───────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────

  console.log('the level are', accountDetails?.level)
  return (
    <Box
      maxH="75vh"
      overflowY="auto"
      pr={3}
      sx={{
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-track": { bg: "transparent" },
        "&::-webkit-scrollbar-thumb": { bg: "gray.300", borderRadius: "full" },
      }}
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        color="blue.600"
        mb={4}
        textTransform="uppercase"
        letterSpacing="wider"
      >
        General Header Data
      </Text>
      <Grid gridTemplateColumns={"1fr 1fr 1fr"} gap={4}>
        <CustomInput
          name="region"
          label="Region"
          type="select"
          isBoxSelect
          isRequired={true}
          value={form.region}
          onChange={(val: any) => updateForm({ region: val })}
          options={dynamicRegions.length > 0 ? dynamicRegions : []}
          disabled={accountDetails?.level === "level-1" ? false : true}
        />
        <CustomInput
          name="companyCode"
          label="Company Code"
          type="select"
          isBoxSelect
          value={form.companyCode}
          onChange={(val: any) => updateForm({ companyCode: val })}
          options={COMPANY_CODES.map((code) => {
            const company = companyOptions.find(c => c.value === code);
            return {
              label: company?.label ? `${company?.label} (${code})` : code,
              value: code
            };
          })} isRequired={true}
          disabled={disabledOtherUserField}
        />
        <FormControl position="relative" ref={vendorContainerRef}>
          <FormLabel fontSize="sm" mb={2}>
            Vendor Code / Name
          </FormLabel>
          <HStack>
            <Input
              {...editableInputProps}
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              onFocus={() => {
                if (vendorList.length > 0 && vendorSearch.length >= 2)
                  setShowVendorList(true);
              }}
              placeholder="Search..."
              isRequired={true}
              disabled={disabledOtherUserField}
            />
            {isVendorLoading && <Spinner size="xs" color="blue.500" />}
          </HStack>
          {showVendorList && (
            <Portal>
              <Box
                ref={dropdownRef}
                position="fixed"
                top={
                  vendorContainerRef.current?.getBoundingClientRect()
                    ? vendorContainerRef.current.getBoundingClientRect()
                      .bottom + window.scrollY
                    : 0
                }
                left={
                  vendorContainerRef.current?.getBoundingClientRect()
                    ? vendorContainerRef.current.getBoundingClientRect().left +
                    window.scrollX
                    : 0
                }
                width={vendorContainerRef.current?.clientWidth || "300px"}
                zIndex={1500}
                bg="white"
                border="1px solid"
                borderColor="blue.200"
                borderRadius="md"
                boxShadow="2xl"
                maxH="250px"
                overflowY="auto"
              >
                <List variant="unstyled">
                  {vendorList.length > 0 ? (
                    vendorList.map((v, i) => (
                      <ListItem
                        key={i}
                        px={3}
                        py={3}
                        borderBottom="1px solid"
                        borderColor="gray.50"
                        _hover={{ bg: "blue.50", cursor: "pointer" }}
                        // Use onMouseDown to capture selection before click-outside logic fires on input blur
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectVendor(v);
                        }}
                      >
                        <Text fontSize="xs" fontWeight="bold" color="blue.700">
                          {v.vendorCode || "N/A"}
                        </Text>
                        <Text fontSize="2xs" color="gray.600">
                          {v.supplierName || v.label || "N/A"}
                        </Text>
                        {(v.companyCode || form.companyCode) && (
                          <Text
                            fontSize="2xs"
                            color="gray.400"
                            fontStyle="italic"
                          >
                            Company:{" "}
                            {typeof v.companyCode === "object"
                              ? v.companyCode?.value
                              : v.companyCode || form.companyCode?.value || ""}
                          </Text>
                        )}
                      </ListItem>
                    ))
                  ) : (
                    <Box px={3} py={3}>
                      <Text fontSize="xs" color="gray.500">
                        No vendors found.
                      </Text>
                    </Box>
                  )}
                </List>
              </Box>
            </Portal>
          )}
        </FormControl>

        <CustomInput
          name="vendorName"
          label="Selected Vendor Name"
          value={form.vendorName || ""}
          disabled={true}
          placeholder="Select vendor from list..."
          isRequired={true}
        />
        <CustomInput
          name="vendorAddress"
          label="Vendor Address"
          value={form.vendorAddress || ""}
          disabled={true}
          placeholder="Select vendor from list..."
          isRequired={true}
        />
        <CustomInput
          name="invoiceNo"
          label="Invoice No."
          value={form.invoiceNo || ""}
          onChange={(e: any) => updateForm({ invoiceNo: e.target.value })}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="invoiceDate"
          label="Invoice Date"
          type="date"
          value={form.invoiceDate || ""}
          onChange={(e: any) => updateForm({ invoiceDate: e.target.value })}
          isRequired={true}
          maxDate={new Date().toISOString().split("T")[0]}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="grandTotal"
          label="Grand Total"
          type="number"
          value={form.grandTotal || ""}
          onChange={(e: any) => updateForm({ grandTotal: e.target.value })}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="paymentDueDate"
          label="Payment Due Date"
          type="date"
          value={form.paymentDueDate || ""}
          onChange={(e: any) => updateForm({ paymentDueDate: e.target.value })}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="taxIdenficationNumber"
          label="Tax Identification Number (vendor)"
          type="text"
          placeholder="Tax indentification Number"
          value={form.taxIdenficationNumber || ""}
          onChange={(e: any) => updateForm({ taxIdenficationNumber: e.target.value })}
          isRequired={true}
          disabled={(accountDetails?.level === "level-1" || accountDetails?.level === "level-2" || accountDetails?.level === "admin") ? false : true}
        />
        {/* <CustomInput
          name="gstPartner"
          label="GST Partner"
          value={form.gstPartner || ""}
          onChange={(e: any) => updateForm({ gstPartner: e.target.value })}
          isRequired={true}
          disabled={disabledOtherUserField}
        /> */}
        {/* <CustomInput
          name="postingDate"
          label="Posting Date"
          type="date"
          value={form.postingDate || ""}
          onChange={(e: any) => updateForm({ postingDate: e.target.value })}
          style={{display:'none'}}
        /> */}
        {/* <CustomInput
          name="exchangeRate"
          label="Exchange Rate"
          type="number"
          value={form.exchangeRate || ""}
          onChange={(e: any) => updateForm({ exchangeRate: e.target.value })}
          rest={{ step: "0.0001" }}
          disabled={disabledOtherUserField}
        /> */}
        <CustomInput
          name="invoiceType"
          label="Invoice Type"
          type="select"
          isBoxSelect
          value={form.invoiceType}
          onChange={(val: any) => handleInvoiceTypeChange(val)}
          options={invoiceTypeOptions}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="currency"
          label="Currency"
          type="select"
          isBoxSelect
          value={form.currency}
          onChange={(val: any) => updateForm({ currency: val })}
          options={addNA(currencyOptions)}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="headerText"
          label="Header Text"
          value={form.headerText || ""}
          onChange={(e: any) => updateForm({ headerText: e.target.value })}
          isRequired={true}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />

        <CustomInput
          name="referenceKey1"
          label="Reference Key 1"
          style={{ display: "none" }}
          value={form.referenceKey1 || ""}
          onChange={(e: any) => updateForm({ referenceKey1: e.target.value })}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="referenceKey2"
          label="Reference Key 2"
          style={{ display: "none" }}

          value={form.referenceKey2 || ""}
          onChange={(e: any) => updateForm({ referenceKey2: e.target.value })}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
        <CustomInput
          name="referenceKey3"
          label="Reference Key 3"
          style={{ display: "none" }}

          value={form.referenceKey3 || ""}
          onChange={(e: any) => updateForm({ referenceKey3: e.target.value })}
          disabled={disabledOtherUserField && accountDetails?.level !== "admin"}
        />
      </Grid>
      <Divider mb={4} mt={4} />
      <Box mt={5}>
        <HStack justify="space-between" mb={5}>
          <Text fontSize="lg" fontWeight="semibold" color="blue.700">
            Line Items
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={addLineItem}
            display={
              disabledOtherUserField ||
                !["level-1", "level-2", "admin"].includes(accountDetails?.level)
                ? "none"
                : undefined
            }>
            Add Line Item
          </Button>
        </HStack>
        {form.lineItems.length > 0 ? (
          <Box
            borderWidth="1px"
            borderRadius="md"
            overflowX="auto"
            sx={{
              "&::-webkit-scrollbar": { height: "8px" },
              "&::-webkit-scrollbar-track": { bg: "gray.100" },
              "&::-webkit-scrollbar-thumb": {
                bg: "gray.400",
                borderRadius: "full",
              },
            }}
          >
            <Table variant="simple" size="sm" minW="100%">
              <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                <Tr>
                  {(accountDetails?.level === "level-1" || accountDetails?.level === "level-3" || accountDetails?.level === "admin") && <Th>Plant Code</Th>}

                  {(accountDetails?.level === "level-3" || accountDetails?.level === "admin") && (
                    <Th minW="180px">Bussiness Place</Th>
                  )}
                  {(["level-5", "level-6", "level-7"].includes(
                    accountDetails?.level,
                  ) ||
                    accountDetails?.level === "admin") && (
                      <Th minW="180px">Tds Section Type</Th>
                    )}
                  {(["level-4", "level-5", "level-6", "level-7"].includes(
                    accountDetails?.level,
                  ) ||
                    accountDetails?.level === "admin") && (
                      <>
                        <Th minW="180px">TDS Type</Th>
                        <Th minW="160px">GL Amount</Th>
                        <Th minW="130px">TDS %</Th>
                        <Th minW="150px">TDS Amount</Th>
                        <Th minW="220px">Remarks</Th>
                      </>
                    )}
                  {(accountDetails?.level?.split("-")[1] >= 3 ||
                    accountDetails?.level === "admin") && (
                      <>
                        <Th minW="160px">Tax Code</Th>
                        <Th minW="180px">GST Percentage</Th>
                        <Th minW="180px">HSN/SAC Code</Th>
                      </>
                    )}
                  <Th minW="220px">Profit Center</Th>
                  <Th minW="220px">Cost Center</Th>

                  {(accountDetails?.level === "level-1" ||
                    accountDetails?.level === "level-2") && (
                      <>
                        <Th minW="180px" display="none">Plant Code</Th>
                        <Th minW="180px" display="none">Business Place</Th>
                      </>
                    )}

                  <Th minW="220px">GL Code</Th>
                  <Th minW="280px">GL Description</Th>
                  <Th minW="220px">Internal Order</Th>
                  {!(
                    ["level-4", "level-5", "level-6", "level-7"].includes(
                      accountDetails?.level,
                    ) || accountDetails?.level === "admin"
                  ) && <Th minW="160px">GL Amount</Th>}
                  <Th minW="300px">Line Narration</Th>
                  <Th minW="180px">Business Area</Th>
                  <Th minW="180px">Dr/Cr</Th>
                  {accountDetails?.level === "level-3" && (
                    <Th display="none" minW="180px">Business Place</Th>
                  )}
                  {(!(
                    accountDetails?.level === "level-1" ||
                    accountDetails?.level === "level-2" ||
                    accountDetails?.level === "level-3"
                  ) ||
                    accountDetails?.level === "admin") && (
                      <>
                        <Th display="none" minW="180px">Plant Code</Th>
                        <Th minW="180px" display="none">Business Place</Th>
                      </>
                    )}
                  <Th minW="70px" textAlign="center">
                    Action
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {form.lineItems.map((item: LineItem, index: number) => (
                  <Tr key={item.rowId || index} _hover={{ bg: "gray.50" }}>
                    {(accountDetails?.level === "level-1" || accountDetails?.level === "level-3" || accountDetails?.level === "admin") && <Td>
                      <HStack>
                        <CustomInput
                          name={`plant-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.plantCode}
                          onChange={(val: any) =>
                            handlePlantCodeChange(index, val)
                          }
                          options={addWithoutNA(plantOptions)}
                          placeholder="Select..."
                          // disabled={
                          //   (accountDetails?.level !== "level-1" &&
                          //     accountDetails?.level !== "level-2") ||
                          //   disabledOtherUserField ||
                          //   ["level-5", "level-6", "level-7"].includes(
                          //     accountDetails?.level,
                          //   )
                          // }
                          style={{ minWidth: "280px" }}

                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => fetchPlantOptions(true)}
                          isDisabled={
                            (accountDetails?.level !== "level-1" &&
                              accountDetails?.level !== "level-2") ||
                            disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            ) ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>}
                    {(accountDetails?.level === "level-3" || accountDetails?.level === "admin") && (
                      <Td>
                        <HStack>
                          <CustomInput
                            name={`businessPlace-${index}`}
                            type="select"
                            isBoxSelect
                            value={item.businessPlace}
                            onChange={(val: any) =>
                              updateLineItem(index, { businessPlace: val })
                            }
                            options={addWithoutNA(businessPlaceOptions || [])}
                            placeholder="Select..."
                            disabled={
                              (accountDetails?.level?.split("-")[1] > 3 ||
                                // disabledOtherUserField ||
                                ["level-5", "level-6", "level-7"].includes(
                                  accountDetails?.level,
                                ))
                            }
                            style={{ minWidth: "280px" }}
                          />
                          <IconButton
                            aria-label="Refresh"
                            icon={<RepeatIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => {
                              setIsOptionsLoading(true);
                              fetchBusinessPlaceOptions(
                                true
                              );
                            }}
                            isDisabled={
                              (accountDetails?.level !== "level-1" &&
                                accountDetails?.level !== "level-2") ||
                              !item.plantCode ||
                              disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              ) ||
                              isOptionsLoading
                            }
                          />
                        </HStack>
                      </Td>
                    )}
                    {(["level-5", "level-6", "level-7"].includes(
                      accountDetails?.level,
                    ) ||
                      accountDetails?.level === "admin") && (
                        <Td>
                          <CustomInput
                            name={`tdsSectionType-${index}`}
                            type="select"
                            isBoxSelect
                            value={item.tdsSectionType}
                            onChange={(val: any) =>
                              updateLineItem(index, { tdsSectionType: val })
                            }
                            options={(item.tdsAmount == null || String(item.tdsAmount).trim() === "" || isNaN(Number(item.tdsAmount)) || Number(item.tdsAmount) === 0) ? [
                              { label: "NA", value: "NA" }
                            ] : [
                              { label: "NA", value: "NA" },
                              { label: "Net-up", value: "net-up" },
                              { label: "Gross-up", value: "gross-up" }
                            ]}
                            placeholder="Select..."
                            disabled={
                              item.tdsType?.value === "TDS NA" ||
                              Number(item.tdsPercentage || 0) === 0 ||
                              (accountDetails?.level !== "level-5" &&
                                accountDetails?.level !== "admin")
                            }
                          />
                        </Td>
                      )}
                    {(["level-4", "level-5", "level-6", "level-7"].includes(
                      accountDetails?.level,
                    ) ||
                      accountDetails?.level === "admin") && (
                        <>
                          <Td>
                            <CustomInput
                              name={`tdsType-${index}`}
                              type="select"
                              isBoxSelect
                              value={item.tdsType}
                              onChange={(val: any) => {
                                if (val.value === "TDS NA") {
                                  updateLineItem(index, { tdsType: val, tdsPercentage: 0, tdsAmount: 0, tdsSectionType: { label: "NA", value: "NA" } })
                                } else {
                                  updateLineItem(index, { tdsType: val })
                                }
                              }}
                              options={[
                                { label: "DTAA", value: "DTAA" },
                                { label: "Section 195", value: "Section 195" },
                                { label: "TDS NA", value: "TDS NA" },
                              ]}
                              placeholder="Select..."
                              disabled={
                                ["level-5", "level-6", "level-7"].includes(
                                  accountDetails?.level,
                                ) && accountDetails?.level !== "admin"
                              }
                            />
                          </Td>
                          <Td>
                            <CustomInput
                              name={`amt-${index}`}
                              type="number"
                              value={item.glAmount || ""}
                              onChange={(e: any) => {
                                const val = e.target.value;
                                const tdsAmt =
                                  (Number(val || 0) *
                                    Number(item.tdsPercentage || 0)) /
                                  100;
                                updateLineItem(index, {
                                  glAmount: val,
                                  tdsAmount: tdsAmt.toFixed(2),
                                });
                              }}
                              disabled={
                                (disabledOtherUserField ||
                                  ["level-5", "level-6", "level-7"].includes(
                                    accountDetails?.level,
                                  )) &&
                                accountDetails?.level !== "admin"
                              }
                            />
                          </Td>
                          <Td>
                            <CustomInput
                              name={`tdsPercent-${index}`}
                              type="number"
                              value={item.tdsPercentage === 0 ? 0 : item.tdsPercentage || ""}
                              onChange={(e: any) => {
                                const p = e.target.value;
                                const amt =
                                  (Number(item.glAmount || 0) * Number(p || 0)) /
                                  100;
                                updateLineItem(index, {
                                  tdsPercentage: p,
                                  tdsAmount: amt.toFixed(2),
                                  tdsSectionType: Number(p) === 0 ? { label: "NA", value: "NA" } : item.tdsSectionType?.value !== "NA" ? item.tdsSectionType : { label: "NA", value: "NA" }
                                });
                              }}
                              disabled={item.tdsType?.value === "TDS NA" ||
                                ["level-5", "level-6", "level-7"].includes(
                                  accountDetails?.level,
                                ) && accountDetails?.level !== "admin"
                              }
                            />
                          </Td>
                          <Td>
                            <CustomInput
                              name={`tdsAmount-${index}`}
                              type="number"
                              value={item.tdsAmount || (item.tdsAmount === 0 ? 0 : "")}
                              onChange={(e: any) => {
                                updateLineItem(index, {
                                  tdsAmount: e.target.value,
                                });
                              }}
                              // disabled={
                              //   item.tdsType?.value === "TDS NA" ||
                              //   (["level-5", "level-6", "level-7"].includes(
                              //     accountDetails?.level,
                              //   ) && accountDetails?.level !== "admin")
                              // }
                              disabled={true}
                            />
                          </Td>
                          <Td>
                            <CustomInput
                              name={`tdsRemarks-${index}`}
                              value={item.tdsRemarks || ""}
                              onChange={(e: any) => {
                                if (e.target.value.length <= 40) {
                                  updateLineItem(index, {
                                    tdsRemarks: e.target.value,
                                  });
                                }
                              }}
                              disabled={
                                ["level-5", "level-6", "level-7"].includes(
                                  accountDetails?.level,
                                ) && accountDetails?.level !== "admin"
                              }
                            />
                          </Td>
                        </>
                      )}
                    {(accountDetails?.level?.split("-")[1] >= 3 ||
                      accountDetails?.level === "admin") && (
                        <>
                          <Td>
                            <HStack>
                              <CustomInput
                                name={`tax-${index}`}
                                type="select"
                                isBoxSelect
                                value={item.taxCode}
                                onChange={(val: any) => {

                                  const taxSum = (val?.taxCodes || []).reduce(
                                    (acc: number, curr: any) =>
                                      acc + Number(curr || 0),
                                    0,
                                  );
                                  updateLineItem(index, {
                                    taxCode: val,
                                    gstPercentage: extractPercentageFromLabel(val.label) || taxSum.toFixed(3),
                                  });
                                }}
                                options={addNAObject(taxOptions).filter((it: any) => it.value !== "NA")}
                                placeholder="Select..."
                                disabled={
                                  accountDetails?.level !== "level-3" &&
                                  accountDetails?.level !== "admin"
                                }
                                style={{ minWidth: "320px" }}
                              />
                              <IconButton
                                aria-label="Refresh"
                                icon={<RepeatIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => fetchTaxOptions(true)}
                                isDisabled={
                                  (accountDetails?.level !== "level-3" &&
                                    accountDetails?.level !== "admin") ||
                                  isOptionsLoading
                                }
                              />
                            </HStack>
                          </Td>
                          <Td>
                            <CustomInput
                              name={`gstPercentage-${index}`}
                              value={item.gstPercentage || ""}
                              onChange={(e: any) =>
                                updateLineItem(index, {
                                  gstPercentage: e.target.value,
                                })
                              }
                              disabled={
                                accountDetails?.level !== "level-3" &&
                                accountDetails?.level !== "admin"
                              }
                            />
                          </Td>
                          <Td>
                            <CustomInput
                              name={`hsn-${index}`}
                              value={item.hsnCode || ""}
                              onChange={(e: any) =>
                                updateLineItem(index, { hsnCode: e.target.value })
                              }
                              disabled={
                                accountDetails?.level !== "level-3" &&
                                accountDetails?.level !== "admin"
                              }
                              style={{ minWidth: "240px" }}
                            />
                          </Td>
                        </>
                      )}
                    <Td>
                      <HStack>
                        <CustomInput
                          name={`pc-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.profitCenter}
                          onChange={(val: any) =>
                            handleProfitCenterChange(index, val)
                          }
                          options={addWithoutNA(pcOptions)}
                          placeholder="Select..."
                          disabled={
                            (disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                            accountDetails?.level !== "admin"
                          }
                          style={{ minWidth: "280px" }}
                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => fetchProfitCenterOptions(true)}
                          isDisabled={
                            ((disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                              accountDetails?.level !== "admin") ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <HStack>
                        <CustomInput
                          name={`cc-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.costCenter}
                          onChange={(val: any) =>
                            handleCostCenterChange(index, val)
                          }
                          options={addWithoutNA(item.costCenterOptions || [])}
                          disabled={
                            !item.profitCenter ||
                            disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            )
                          }
                          placeholder="Select..."
                          style={{ minWidth: "280px" }}

                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => {
                            setIsOptionsLoading(true);
                            fetchCostCenterOptions(
                              index,
                              item.profitCenter,
                              true,
                            );
                          }}
                          isDisabled={
                            !item.profitCenter ||
                            disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            ) ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>

                    {(accountDetails?.level === "level-1" ||
                      accountDetails?.level === "level-2") && (
                        <>
                          <Td display="none">
                            <HStack>
                              <CustomInput
                                name={`plant-${index}`}
                                type="select"
                                isBoxSelect
                                value={item.plantCode}
                                onChange={(val: any) =>
                                  handlePlantCodeChange(index, val)
                                }
                                options={addWithoutNA(plantOptions)}
                                placeholder="Select..."
                                disabled={
                                  (accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                  disabledOtherUserField ||
                                  ["level-5", "level-6", "level-7"].includes(
                                    accountDetails?.level,
                                  )
                                }
                                style={{ minWidth: "280px" }}

                              />
                              <IconButton
                                aria-label="Refresh"
                                icon={<RepeatIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => fetchPlantOptions(true)}
                                isDisabled={
                                  (accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                  disabledOtherUserField ||
                                  ["level-5", "level-6", "level-7"].includes(
                                    accountDetails?.level,
                                  ) ||
                                  isOptionsLoading
                                }
                              />
                            </HStack>
                          </Td>
                          <Td display="none">
                            <HStack>
                              <CustomInput
                                name={`businessPlace-${index}`}
                                type="select"
                                isBoxSelect
                                value={item.businessPlace}
                                onChange={(val: any) =>
                                  updateLineItem(index, { businessPlace: val })
                                }
                                options={addWithoutNA(item.businessPlaceOptions || [])}
                                placeholder="Select..."
                                disabled={
                                  (accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                  !item.plantCode ||
                                  disabledOtherUserField ||
                                  ["level-5", "level-6", "level-7"].includes(
                                    accountDetails?.level,
                                  )
                                }
                                style={{ minWidth: "280px" }}
                              />
                              <IconButton
                                aria-label="Refresh"
                                icon={<RepeatIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => {
                                  setIsOptionsLoading(true);
                                  fetchBusinessPlaceOptions(
                                    true
                                  );
                                }}
                                isDisabled={
                                  (accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                  !item.plantCode ||
                                  disabledOtherUserField ||
                                  ["level-5", "level-6", "level-7"].includes(
                                    accountDetails?.level,
                                  ) ||
                                  isOptionsLoading
                                }
                              />
                            </HStack>
                          </Td>
                        </>
                      )}

                    <Td>
                      <HStack>
                        <CustomInput
                          name={`gl-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.glCode}
                          onChange={(val: any) =>
                            handleGlCodeChange(index, val)
                          }
                          options={addNA(glOptions)}
                          placeholder="Select..."
                          disabled={
                            disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            )
                          }
                          style={{ minWidth: "240px" }}

                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => fetchGlOptions(true)}
                          isDisabled={
                            disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            ) ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <CustomInput
                        value={item.glDescription || "-"}
                        name={`glDescription-${index}`}
                        disabled={true}
                      />
                    </Td>
                    <Td>
                      <HStack>
                        <CustomInput
                          name={`io-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.internalOrder}
                          onChange={(val: any) =>
                            updateLineItem(index, { internalOrder: val })
                          }
                          options={addWithoutNA(item.internalOrderOptions || [])}
                          disabled={
                            !item.profitCenter ||
                            !item.glCode ||
                            ((disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                              accountDetails?.level !== "admin")
                          }
                          placeholder="Select..."
                          style={{ minWidth: "240px" }}
                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => {
                            setIsOptionsLoading(true);
                            fetchInternalOrderOptions(
                              index,
                              form.companyCode,
                              item.profitCenter,
                              item.glCode,
                              true,
                            );
                          }}
                          isDisabled={
                            !item.profitCenter ||
                            !item.glCode ||
                            ((disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                              accountDetails?.level !== "admin") ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>
                    {!(
                      ["level-4", "level-5", "level-6", "level-7"].includes(
                        accountDetails?.level,
                      ) || accountDetails?.level === "admin"
                    ) && (
                        <Td>
                          <CustomInput
                            name={`amt-${index}`}
                            type="number"
                            value={item.glAmount || ""}
                            onChange={(e: any) => {
                              const val = e.target.value;
                              const tdsAmt = customDecimalRoundSafeFunction(
                                (Number(val || 0) *
                                  Number(item.tdsPercentage || 0)) /
                                100,
                              );
                              updateLineItem(index, {
                                glAmount: val,
                                tdsAmount: tdsAmt,
                              });
                            }}
                            disabled={
                              (disabledOtherUserField ||
                                ["level-5", "level-6", "level-7"].includes(
                                  accountDetails?.level,
                                )) &&
                              accountDetails?.level !== "admin"
                            }
                            style={{ minWidth: "240px" }}

                          />
                        </Td>
                      )}

                    <Td>
                      <CustomInput
                        name={`narr-${index}`}
                        value={item.lineNarration || ""}
                        onChange={(e: any) =>
                          updateLineItem(index, {
                            lineNarration: e.target.value,
                          })
                        }
                        disabled={
                          (disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            )) &&
                          accountDetails?.level !== "admin"
                        }
                        style={{ minWidth: "240px" }}

                      />
                    </Td>
                    <Td>
                      <HStack>
                        <CustomInput
                          style={{ minWidth: "240px" }}

                          name={`ba-${index}`}
                          type="select"
                          isBoxSelect
                          value={item.businessArea}
                          onChange={(val: any) =>
                            updateLineItem(index, { businessArea: val })
                          }
                          options={addWithoutNA(item.businessAreaOptions || [])}
                          disabled={
                            !item.costCenter ||
                            ((disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                              accountDetails?.level !== "admin")
                          }
                          placeholder="Select..."
                        />
                        <IconButton
                          aria-label="Refresh"
                          icon={<RepeatIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => {
                            setIsOptionsLoading(true);
                            fetchBusinessAreaOptions(
                              index,
                              item.profitCenter,
                              item.costCenter,
                              true,
                            );
                          }}
                          isDisabled={
                            !item.costCenter ||
                            ((disabledOtherUserField ||
                              ["level-5", "level-6", "level-7"].includes(
                                accountDetails?.level,
                              )) &&
                              accountDetails?.level !== "admin") ||
                            isOptionsLoading
                          }
                        />
                      </HStack>
                    </Td>
                    <Td>
                      <CustomInput
                        style={{ minWidth: "240px" }}
                        name={`dc-${index}`}
                        type="select"
                        isBoxSelect
                        value={item.drCrIndicator}
                        onChange={(val: any) =>
                          updateLineItem(index, { drCrIndicator: val })
                        }
                        options={[
                          { label: "Debit", value: "S" },
                          { label: "Credit", value: "H" },
                        ]}
                        placeholder="Select"
                        disabled={
                          ((disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            )) &&
                            accountDetails?.level !== "admin") ||
                          ["KG", "KR"].includes(form.invoiceType?.value)
                        }
                      />
                    </Td>
                    {accountDetails?.level === "level-3" && (
                      <Td display="none">
                        <HStack>
                          <CustomInput
                            style={{ minWidth: "240px" }}
                            name={`businessPlace-${index}`}
                            type="select"
                            isBoxSelect
                            value={item.businessPlace}
                            onChange={(val: any) =>
                              updateLineItem(index, { businessPlace: val })
                            }
                            options={addWithoutNA(item.businessPlaceOptions || [])}
                            placeholder="Select..."
                            disabled={
                              (!item.plantCode ||
                                disabledOtherUserField) &&
                              accountDetails?.level !== "admin"
                            }

                          />
                          <IconButton
                            aria-label="Refresh"
                            icon={<RepeatIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() =>
                              fetchBusinessPlaceOptions(
                                true
                              )
                            }
                            isDisabled={
                              (!item.plantCode ||
                                (disabledOtherUserField &&
                                  accountDetails?.level !== "admin")) ||
                              isOptionsLoading
                            }
                          />
                        </HStack>
                      </Td>
                    )}
                    {(!(
                      accountDetails?.level === "level-1" ||
                      accountDetails?.level === "level-2" ||
                      accountDetails?.level === "level-3"
                    ) ||
                      accountDetails?.level === "admin") && (
                        <>
                          <Td display="none">
                            <HStack>
                              <CustomInput
                                style={{ minWidth: "240px" }}

                                name={`plant-${index}`}
                                type="select"
                                isBoxSelect
                                value={item.plantCode}
                                onChange={(val: any) =>
                                  handlePlantCodeChange(index, val)
                                }
                                options={addWithoutNA(plantOptions)}
                                placeholder="Select..."
                                disabled={
                                  ((accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                    disabledOtherUserField) &&
                                  accountDetails?.level !== "admin"
                                }

                              />
                              <IconButton
                                aria-label="Refresh"
                                icon={<RepeatIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => fetchPlantOptions(true)}
                                isDisabled={
                                  (((accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                    disabledOtherUserField) &&
                                    accountDetails?.level !== "admin") ||
                                  isOptionsLoading
                                }
                              />
                            </HStack>
                          </Td>
                          <Td display="none">
                            <HStack>
                              <CustomInput
                                style={{ minWidth: "240px" }}
                                name={`businessPlace-${index}`}
                                type="select"
                                isBoxSelect
                                value={item.businessPlace}
                                onChange={(val: any) =>
                                  updateLineItem(index, { businessPlace: val })
                                }
                                options={addWithoutNA(item.businessPlaceOptions || [])}
                                placeholder="Select..."
                                disabled={
                                  ((accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                    !item.plantCode ||
                                    disabledOtherUserField) &&
                                  accountDetails?.level !== "admin"
                                }

                              />
                              <IconButton
                                aria-label="Refresh"
                                icon={<RepeatIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() =>
                                  fetchBusinessPlaceOptions(
                                    true
                                  )
                                }
                                isDisabled={
                                  (((accountDetails?.level !== "level-1" &&
                                    accountDetails?.level !== "level-2") ||
                                    !item.plantCode ||
                                    disabledOtherUserField) &&
                                    accountDetails?.level !== "admin") ||
                                  isOptionsLoading
                                }
                              />
                            </HStack>
                          </Td>
                        </>
                      )}


                    <Td>
                      <IconButton
                        aria-label="del"
                        icon={<CloseIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removeLineItem(index)}
                        disabled={
                          (disabledOtherUserField ||
                            ["level-5", "level-6", "level-7"].includes(
                              accountDetails?.level,
                            )) &&
                          accountDetails?.level !== "admin"
                        }
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Text textAlign="center" py={10}>
            No items
          </Text>
        )}
      </Box>
    </Box>
  );
}